import { useCallback, useEffect } from 'react';
import { Button, makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { openPermissionPage } from 'src/utils/open-permission-page';
import { useAuthorizationStep } from '.';
import { checkProjectAuthorizationService } from 'src/services/appgami-api/check-project-authorization';

let debounceTimeout: NodeJS.Timeout | null = null;
const call_limit = 30;
let callCount = 0;

export function Authorization() {
	const styles = useStyles();

	const { organization, onClose, setAuthorization, adminUrl, setStep } = useAuthorizationStep();

	const checkProjectAuthorization = useCallback(async () => {
		const result = await checkProjectAuthorizationService({
			organizationName: organization.name,
		});

		if (result.type === 'success') {
			setAuthorization({
				service: result.data.servicePrincipalAuthorization,
				user: result.data.userAuthorization,
			});

			return result.data;
		}

		return null;
	}, [organization.name]);

	const goToPermissions = useCallback(() => {
		openPermissionPage({ organizationName: organization.name, organizationId: organization.id });

		const clearDebounceTimeout = () => {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
		};

		const callReloadAuthClientInit = () => {
			clearDebounceTimeout();

			if (callCount < call_limit) {
				debounceTimeout = setTimeout(async () => {
					const result = await checkProjectAuthorization();

					if (!result?.userAuthorization) {
						callCount++;
						callReloadAuthClientInit();
					}

					if (result?.userAuthorization) {
						clearDebounceTimeout();
						setStep(3);
					}
				}, 3000);
			}
		};

		callReloadAuthClientInit();
	}, [
		organization.name,
		organization.id,
	]);

	useEffect(() => {
		return () => {
			if (debounceTimeout) {
				clearTimeout(debounceTimeout);
			}
		};
	},[]);

	return (
		<div className={styles.root}>
			<div className={styles.content}>
				<p>
					{'To run automations, Appgami Automation needs your permission to connect to Azure DevOps on your behalf. That\'s what authorizing does: it lets Appgami Automation securely listen for changes and run your rules without any manual work from you.'}
				</p>
				<div className={styles.messageBarWrapper}>
					<MessageBar intent='warning' layout="multiline">
						<MessageBarBody>
							{'On the next screen, make sure to check "Consent on behalf of your organization" before continuing. If this isn\'t checked, only you will be authorized - teammates without admin access will stay blocked when they try to authorize later.'}
						</MessageBarBody>
					</MessageBar>
				</div>
			</div>
			<div className={styles.bottomRoot}>
				<Button
					appearance="primary"
					onClick={() => {
						goToPermissions();
					}}
				>
          Authorize
				</Button>
				{onClose && (
					<Button
						appearance="transparent"
						onClick={onClose}
					>
						<span className={styles.notNowText}>
              Not now
						</span>
					</Button>
				)}
				<>
        
				</>
				<span className={styles.notAdminText}>
          Not an admin? Share this with someone who is: <br />
				</span>
				<a
					href={adminUrl}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.notAdminTextAnchor}
				>
					{adminUrl}
				</a>
				<span className={styles.Bottomline} />
				<div className={styles.BottombuttonArea}>
					<span className={styles.bottomButtonAreaText}>
						{'Authorizing installs the Appgami Automation app in your organization\'s Microsoft Entra ID tenant. You\'ll need one of the following roles to complete this step: '}
						<strong className={styles.bottomButtonAreaTextStrong}>Cloud Application Administrator</strong>
						{', '}
						<strong className={styles.bottomButtonAreaTextStrong}>Application Administrator</strong>
						{', or '}
						<strong className={styles.bottomButtonAreaTextStrong}>Global Administrator</strong>
						{'.'}
					</span>
				</div>
				<div>
					<span className={styles.helpText}>
						Need help? <a href='https://appgami.atlassian.net/servicedesk/customer/portal/2' target='_blank' rel='noopener noreferrer'>Get in touch</a> with Appgami or email us at <a target='_blank' rel='noopener noreferrer' href='mailto:support@appgami.com'>support@appgami.com</a>
					</span>
				</div>
			</div>
		</div>
	);
}

const useStyles = makeStyles({
	root: {
		padding: '32px 24px',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'column',
	},
	content: {
		maxWidth: '600px',
		textAlign: 'center',
		marginBottom: '32px',
	},
	bottomRoot: {
		width: '100%',
		maxWidth: '600px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		gap: '16px',
	},
	Bottomline: {
		display: 'block',
		width: '100%',
		height: '1px',
		backgroundColor: tokens.colorSubtleBackgroundSelected,
	},
	BottombuttonArea: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: '8px',
		width: '100%',
	},
	messageBarWrapper: {
		boxSizing: 'border-box',
		padding: '32px 0px 0px 0px',
	},
	notNowText: {
		color: tokens.colorNeutralForeground1,
		fontWeight: tokens.fontWeightRegular,
	},
	notAdminText: {
		marginTop: '16px',
		color: tokens.colorNeutralForeground1,
		fontWeight: tokens.fontWeightRegular,
	},
	notAdminTextAnchor: {
		color: tokens.colorBrandForeground1,
		fontWeight: tokens.fontWeightRegular,
		textDecoration: 'underline',
		textAlign: 'center',
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline',
			color: tokens.colorBrandForeground1,
			cursor: 'pointer',
		},
	},
	bottomButtonAreaText: {
		color: tokens.colorNeutralForeground1,
		fontWeight: tokens.fontWeightRegular,
		fontSize: '14px',
		textAlign: 'center',
	},
	bottomButtonAreaTextStrong: {
		color: tokens.colorNeutralForeground1,
		fontWeight: tokens.fontWeightSemibold,
	},
	helpText: {
		color: tokens.colorNeutralForeground1,
		fontWeight: tokens.fontWeightRegular,
		fontSize: '14px',
		textAlign: 'center',
		'& a': {
			color: tokens.colorBrandForeground1,
			fontWeight: tokens.fontWeightRegular,
			textDecoration: 'underline',
			cursor: 'pointer',
		},
	},
});